import { useRef } from 'react';
import { Box, Divider, Drawer, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBackOutlined';
import Close from '@mui/icons-material/CloseOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { CircleHelp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useResizablePanelWidth } from '@/components/useResizablePanelWidth';
import { useDebounce } from '@/hooks/useDebounce';
import { usePageEntityContext } from '@/lib/pageEntityContext';
import { HelpIndexView } from './HelpIndexView';
import { helpPanel, isFieldTarget, isFieldsTarget, useHelpPanel, type HelpTarget } from './helpPanelStore';
import { HelpFieldView, HelpForScreen, HelpPageView } from './HelpViews';
import { HelpFieldsView } from './HelpFieldsView';
import { HelpSearchResults } from './HelpSearchResults';

const DEFAULT_DRAWER_WIDTH = 420;
const MIN_QUERY_LENGTH = 2;

/** The header button that opens the help for the screen. Rendered in both the desktop and the mobile app bar. */
export function HelpButton() {
  const { t } = useTranslation();
  return (
    <IconButton color="inherit" onClick={helpPanel.openForScreen} title={t('help.open')} aria-label={t('help.open')}>
      <CircleHelp size={20} />
    </IconButton>
  );
}

/**
 * The contextual help panel (docs/15-designspecs/User-Help-And-Setup-Guides.md §Contextual help panel):
 * the help pages for the entity on screen, the index of all help pages per module, a search over all
 * help, and page and field views where `help:` links open further help in the panel. A hit opened from
 * the search keeps the search, so going back shows the results again. Rendered once, by the Layout.
 */
export function HelpPanelDrawer() {
  const { t, i18n } = useTranslation();
  const { open, target, browsing, query, module } = useHelpPanel();
  const { entityType, module: entityModule } = usePageEntityContext();
  const debouncedQuery = useDebounce(query.trim(), 250);
  const searchRef = useRef<HTMLInputElement>(null);
  const { width, ResizeHandle } = useResizablePanelWidth('help', {
    defaultWidth: DEFAULT_DRAWER_WIDTH,
    storageScope: 'global',
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={helpPanel.close}
      // Above dialogs: "Read more" in a dialog's refusal opens the panel over it, and closing the panel returns
      // to the dialog as it was. MUI stacks its modals, so the panel, opened last, holds the focus meanwhile.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'transparent' } },
        // The search takes the focus once the panel has slid in. Not autoFocus: the drawer's focus trap
        // moves the focus to the panel itself when it opens, after the input's own autoFocus.
        // The search kept from last time is selected, so typing starts a new one.
        transition: {
          onEntered: () => {
            searchRef.current?.focus();
            searchRef.current?.select();
          },
        },
        paper: {
          sx: { width, display: 'flex', flexDirection: 'column', top: '59px', height: 'calc(100% - 58px)' },
        },
      }}
    >
      <ResizeHandle />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
        {(target || browsing) && (
          <IconButton size="small" onClick={helpPanel.back} title={t('help.back')} aria-label={t('help.back')}>
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <CircleHelp size={20} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          {t('help.title')}
        </Typography>
        <IconButton size="small" onClick={helpPanel.close} title={t('common.close')} aria-label={t('common.close')}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, pb: 1.5 }}>
        <TextField
          fullWidth
          inputRef={searchRef}
          size="small"
          placeholder={t('help.searchPlaceholder')}
          value={query}
          onChange={(e) => helpPanel.search(e.target.value)}
          slotProps={{
            htmlInput: { 'aria-label': t('help.searchPlaceholder') },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, px: 2, py: 1.5 }}>
        <HelpPanelBody
          query={debouncedQuery}
          module={module}
          target={target}
          browsing={browsing}
          entity={entityType}
          entityModule={entityModule}
          language={i18n.language}
          onOpen={helpPanel.openTarget}
        />
      </Box>
    </Drawer>
  );
}

interface HelpPanelBodyProps {
  query: string;
  module: string | null;
  target: HelpTarget | null;
  browsing: boolean;
  entity?: string;
  /** The module of the screen's entity; `module` is the search's module filter. */
  entityModule?: string;
  language: string;
  onOpen: (target: HelpTarget) => void;
}

/**
 * What the panel shows: the page, field or form's fields opened; else the results while there is a search;
 * else the index when browsing or on a screen without an entity; else the help for the screen's entity.
 */
function HelpPanelBody({ query, module, target, browsing, entity, entityModule, language, onOpen }: HelpPanelBodyProps) {
  if (target) return <HelpTargetView target={target} language={language} onOpen={onOpen} />;
  if (query.length >= MIN_QUERY_LENGTH) {
    return <HelpSearchResults query={query} entity={entity} language={language} module={module} onFilter={helpPanel.filterModule} onOpen={onOpen} />;
  }
  if (browsing || !entity) return <HelpIndexView language={language} onOpen={onOpen} />;
  return <HelpForScreen entity={entity} module={entityModule} language={language} onOpen={onOpen} />;
}

interface HelpTargetViewProps {
  target: HelpTarget;
  language: string;
  onOpen: (target: HelpTarget) => void;
}

function HelpTargetView({ target, language, onOpen }: HelpTargetViewProps) {
  if (isFieldTarget(target)) return <HelpFieldView target={target} language={language} onOpen={onOpen} />;
  if (isFieldsTarget(target)) return <HelpFieldsView target={target} />;
  return <HelpPageView target={target} language={language} />;
}
